/**
 * Tool converter for AI SDK integration
 * Converts tool definitions to AI SDK format using pre-built Zod schemas
 */

import { z } from 'zod';
import { tool, zodSchema } from 'ai';
import type { Tool, ToolExecutionContext } from '../types/index.js';
import { getToolSchema } from '../schemas/index.js';
import { executeTool } from '../execution/index.js';
import { logger } from '../../logger.js';

/**
 * Convert tool definitions to AI SDK format
 * Uses pre-built Zod schemas instead of dynamic generation
 * Uses manual Zod-to-JSON-Schema conversion to ensure Bedrock compatibility
 */
export function convertToolsForAI(
  toolDefinitions: Record<string, Tool>,
  userContext: { userId: string; userEmail: string; userToken: string }
): Record<string, any> {
  const tools: Record<string, any> = {};

  for (const toolDef of Object.values(toolDefinitions)) {
    // Get pre-built Zod schema
    const zodSchemaObj = getToolSchema(toolDef.id);
    if (!zodSchemaObj) {
      logger.warn('no Zod schema found for tool', { op: 'convertToolsForAI', toolId: toolDef.id });
      continue;
    }

    // Convert Zod schema to JSON schema using AI SDK's zodSchema helper
    const jsonSchema = zodSchema(zodSchemaObj as any);
    
    // Ensure the schema has type: "object" at the root level (required by Bedrock)
    const schemaWithType = {
      type: 'object' as const,
      ...jsonSchema,
    };

    // Use the tool() wrapper with the converted JSON schema
    tools[toolDef.id] = tool({
      description: toolDef.description,
      parameters: schemaWithType as any,
      needsApproval: !!toolDef.requiresConfirmation,
      execute: async (params: Record<string, any>) => {
        try {
          // Verify handler is loaded before execution
          if (!toolDef.handler) {
            return {
              error: `Tool handler not initialized for ${toolDef.id}. Please try again.`,
            };
          }

          // Execute the tool with real user context
          const result = await executeTool(
            toolDef.id,
            params,
            {
              userId: userContext.userId,
              userEmail: userContext.userEmail,
              userToken: userContext.userToken,
              chatId: `chat-${Date.now()}`,
              messageId: `msg-${Date.now()}`,
            } as ToolExecutionContext
          );

          // Return result in the format expected by the AI SDK
          if (result.type === 'error') {
            return {
              error: result.error?.message || 'Tool execution failed',
            };
          }

          return result.data || result;
        } catch (error) {
          logger.error('tool execution error', error, { op: 'convertToolsForAI', toolId: toolDef.id });
          return {
            error: error instanceof Error ? error.message : 'Unknown error',
          };
        }
      },
    } as any);
  }

  return tools;
}

/**
 * Get available tools for AI SDK
 */
export function getAvailableToolsForAI(toolDefinitions: Record<string, Tool>): Array<{
  id: string;
  name: string;
  description: string;
  schema: z.ZodSchema;
}> {
  return Object.values(toolDefinitions).map((tool) => ({
    id: tool.id,
    name: tool.name,
    description: tool.description,
    schema: getToolSchema(tool.id) || z.object({}),
  }));
}

/**
 * Validate tool parameters using pre-built schemas
 */
export function validateToolParametersWithSchema(
  toolId: string,
  params: Record<string, any>
): { valid: boolean; errors?: string[] } {
  const schema = getToolSchema(toolId);
  if (!schema) {
    return { valid: false, errors: [`No schema found for tool: ${toolId}`] };
  }

  try {
    schema.parse(params);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        valid: false,
        errors: error.issues.map((e: any) => `${e.path.join('.')}: ${e.message}`),
      };
    }
    return { valid: false, errors: ['Unknown validation error'] };
  }
}
